import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  CloudArrowUpIcon,
  DocumentTextIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import ProgressSteps from '../components/ProgressSteps';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AgreementUploadPage = () => {
  const navigate = useNavigate();
  const { setUser, refreshUser } = useAuth();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    tenantName: '',
    landlordName: '',
    propertyAddress: '',
    monthlyRent: '',
    eStampCertificate: '',
  });

  const phone = sessionStorage.getItem('signup_phone');

  const handleFileSelect = async (selectedFile) => {
    setError('');
    setFile(selectedFile);

    // Create preview for images only
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    } else if (selectedFile.type === 'application/pdf') {
      // PDF uploaded - no preview, but accepted
      setPreview(null);
    } else {
      setError('Please upload an image file (JPEG/PNG) or PDF');
      setFile(null);
      return;
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      await handleFileSelect(droppedFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleConfirmAndSignup = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.tenantName || !formData.propertyAddress || !formData.monthlyRent) {
      setError('Please fill in all required fields (marked with *)');
      return;
    }

    setLoading(true);

    try {
      // Update user profile with rental agreement details
      const response = await axios.put(
        `${API_URL}/users/profile`,
        {
          name: formData.tenantName,
          propertyAddress: formData.propertyAddress,
          monthlyRent: parseFloat(formData.monthlyRent),
          landlordName: formData.landlordName || undefined,
          eStampCertificate: formData.eStampCertificate || undefined,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (response.data.user) {
        console.log('User after verification update:', {
          isVerified: response.data.user.isVerified,
          verificationStatus: response.data.user.verificationStatus,
          name: response.data.user.name,
          propertyAddress: response.data.user.propertyAddress,
          monthlyRent: response.data.user.monthlyRent
        });
        
        // Update AuthContext immediately with the response data
        setUser(response.data.user);
        
        // Also refresh from server to ensure consistency
        await refreshUser();
        
        // Show success (could use toast)
        alert('Rental agreement verified! Welcome to LOCU');

        // Navigate after a brief moment to allow state update
        setTimeout(() => {
          if (window.location.pathname === '/profile') {
            window.location.reload();
          } else {
            // Redirect to dashboard
            navigate('/dashboard');
          }
        }, 500);
      } else {
        setError(response.data.message || 'Verification failed');
      }
    } catch (error) {
      console.error('Agreement verification error:', error);
      setError(
        error.response?.data?.message || 'Failed to verify agreement. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const removeFile = () => {
    setFile(null);
    setPreview(null);
    setFormData({
      tenantName: '',
      landlordName: '',
      propertyAddress: '',
      monthlyRent: '',
      eStampCertificate: '',
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-white py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Upload Your Rent Agreement
          </h1>
          <p className="text-lg text-gray-600">
            Upload your rent agreement to start earning 2% LOCU rewards on every
            payment
          </p>
        </div>

        <ProgressSteps currentStep="agreement" />

        <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 space-y-6">
          {/* Upload Section */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary-500 transition-colors"
          >
            {!file ? (
              <div>
                <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors"
                >
                  Choose File
                </label>
                <input
                  id="file-upload"
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={async (e) => {
                    if (e.target.files[0]) {
                      await handleFileSelect(e.target.files[0]);
                    }
                  }}
                />
                <p className="mt-4 text-sm text-gray-500">
                  or drag and drop
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  PNG, JPG, JPEG, or PDF up to 10MB
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {preview && (
                  <div className="relative inline-block max-w-full">
                    <img
                      src={preview}
                      alt="Preview"
                      className="max-h-64 max-w-full rounded-lg border border-gray-200"
                    />
                  </div>
                )}
                <div className="flex items-center justify-center space-x-4">
                  <DocumentTextIcon className="h-8 w-8 text-primary-600" />
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-gray-900">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <button
                    onClick={removeFile}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Agreement Details Form - Manual Entry */}
          {file && (
            <form onSubmit={handleConfirmAndSignup} className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800 font-medium">
                  📝 Please enter your rent agreement details manually.
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label
                    htmlFor="tenantName"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Tenant Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="tenantName"
                    name="tenantName"
                    required
                    value={formData.tenantName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label
                    htmlFor="landlordName"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Landlord Name
                  </label>
                  <input
                    type="text"
                    id="landlordName"
                    name="landlordName"
                    value={formData.landlordName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label
                    htmlFor="propertyAddress"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Property Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="propertyAddress"
                    name="propertyAddress"
                    required
                    rows="3"
                    value={formData.propertyAddress}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label
                    htmlFor="monthlyRent"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Monthly Rent (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    id="monthlyRent"
                    name="monthlyRent"
                    required
                    min="0"
                    step="0.01"
                    value={formData.monthlyRent}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label
                    htmlFor="eStampCertificate"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    E-Stamp Certificate Number (if applicable)
                  </label>
                  <input
                    type="text"
                    id="eStampCertificate"
                    name="eStampCertificate"
                    value={formData.eStampCertificate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setFile(null);
                    setPreview(null);
                    removeFile();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Upload Different File
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Completing Signup...
                    </span>
                  ) : (
                    'Confirm & Signup'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgreementUploadPage;

